import { useCallback, useRef } from 'react';

const TICK_INTERVAL_MS = 500;

export type TranscriptionSilenceTimerOptions = {
   /** Total ms without transcription results before disconnecting. */
   silenceTimeoutMs: number;
   /**
    * How many ms before the timeout to start emitting countdown warnings.
    * E.g. silenceTimeoutMs=30000, silenceWarningMs=10000 → warning starts after 20s of silence.
    */
   silenceWarningMs: number;
   /** Fired repeatedly while in the warning window, with seconds remaining until disconnect. */
   onSilenceSeconds: (secondsRemaining: number) => void;
   /** Fired when a transcription result arrives after a warning was active. */
   onSpeaking: () => void;
   /** Fired when the timeout is reached — caller should close the WebSocket. */
   onTimeout: () => void;
};

export type TranscriptionSilenceTimerHandle = {
   /**
    * Call this every time the transcription service delivers a result (partial or final).
    * Resets the silence clock and clears any active countdown warning.
    */
   notifyActivity: () => void;
   /** Start the silence timer. Call once after the WebSocket opens. */
   start: () => void;
   /** Stop and clean up the timer. Call when the session ends for any reason. */
   stop: () => void;
};

/**
 * Tracks silence based on transcription results rather than raw audio.
 *
 * The timer resets every time a transcription result arrives (partial or final).
 * If no result arrives for silenceTimeoutMs the onTimeout callback fires.
 *
 * This is more reliable than RMS-based detection because:
 * - It is threshold-free — no mic gain or noise floor tuning required.
 * - It works regardless of OS audio normalisation or background noise.
 * - It correctly handles diarization mode where audio processing is disabled.
 *
 * The hook accepts a ref so the caller can update timeout values and callbacks
 * between sessions without re-creating the timer.
 */
export function useTranscriptionSilenceTimer(
   optionsRef: React.MutableRefObject<TranscriptionSilenceTimerOptions>
): TranscriptionSilenceTimerHandle {
   const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
   const silenceSinceRef = useRef<number | null>(null);
   const lastWarningSecondRef = useRef(-1);
   const warningActiveRef = useRef(false);

   const stop = useCallback(() => {
      if (intervalRef.current !== null) {
         clearInterval(intervalRef.current);
         intervalRef.current = null;
      }
      silenceSinceRef.current = null;
      lastWarningSecondRef.current = -1;
      warningActiveRef.current = false;
   }, []);

   const notifyActivity = useCallback(() => {
      // Reset the silence clock
      silenceSinceRef.current = Date.now();
      lastWarningSecondRef.current = -1;
      if (warningActiveRef.current) {
         warningActiveRef.current = false;
         optionsRef.current.onSpeaking();
      }
   }, [optionsRef]);

   const start = useCallback(() => {
      stop();
      silenceSinceRef.current = Date.now();
      lastWarningSecondRef.current = -1;
      warningActiveRef.current = false;

      intervalRef.current = setInterval(() => {
         const silenceSince = silenceSinceRef.current;
         if (silenceSince === null) {
            return;
         }

         const { silenceTimeoutMs, silenceWarningMs, onSilenceSeconds, onTimeout } = optionsRef.current;
         const silentMs = Date.now() - silenceSince;

         if (silentMs >= silenceTimeoutMs) {
            stop();
            onTimeout();
            return;
         }

         const msUntilDisconnect = silenceTimeoutMs - silentMs;
         if (msUntilDisconnect <= silenceWarningMs) {
            warningActiveRef.current = true;
            const secondsRemaining = Math.ceil(msUntilDisconnect / 1000);
            if (secondsRemaining !== lastWarningSecondRef.current) {
               lastWarningSecondRef.current = secondsRemaining;
               onSilenceSeconds(secondsRemaining);
            }
         }
      }, TICK_INTERVAL_MS);
   }, [stop, optionsRef]);

   return { notifyActivity: notifyActivity, start: start, stop: stop };
}
